import { Dialog } from "@base-ui-components/react/dialog";
import { Send, X } from "lucide-react";
import { type Dispatch, type FormEvent, type SetStateAction, useEffect, useState } from "react";
import { ApiError, getSettings, testTelegramSettings, updateSettings, type SettingsInput } from "../api";
import { useToast } from "../toastContext";

interface SettingsDialogProps {
  open: boolean;
  onClose: () => void;
}

interface SettingsForm {
  dailyCheckTime: string;
  checkTimeoutMs: string;
  telegramChatId: string;
  publicSubWorkerOrigin: string;
}

export function SettingsDialog({ open, onClose }: SettingsDialogProps) {
  const [form, setForm] = useState<SettingsForm | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    async function loadSettings() {
      setForm(null);
      setIsLoading(true);
      setIsSaving(false);
      setIsTesting(false);
      setError(null);
      try {
        const response = await getSettings();
        if (cancelled) return;
        setForm({
          dailyCheckTime: response.settings.dailyCheckTime,
          checkTimeoutMs: String(response.settings.checkTimeoutMs),
          telegramChatId: response.settings.telegramChatId,
          publicSubWorkerOrigin: response.settings.publicSubWorkerOrigin
        });
      } catch (requestError) {
        if (!cancelled) setError(readRequestError(requestError, "Failed to load settings"));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    void loadSettings();

    return () => {
      cancelled = true;
    };
  }, [open]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!form || isSaving || isTesting) return;

    const input = settingsInputFromForm(form);
    if (!input) {
      setError("Check timeout must be a whole number");
      return;
    }

    setIsSaving(true);
    setError(null);
    try {
      const response = await updateSettings(input);
      setForm({
        dailyCheckTime: response.settings.dailyCheckTime,
        checkTimeoutMs: String(response.settings.checkTimeoutMs),
        telegramChatId: response.settings.telegramChatId,
        publicSubWorkerOrigin: response.settings.publicSubWorkerOrigin
      });
      toast.showToast({ tone: "success", title: "Settings saved" });
    } catch (requestError) {
      setError(readRequestError(requestError, "Failed to save settings"));
    } finally {
      setIsSaving(false);
    }
  }

  async function handleTestTelegram() {
    if (!form || isTesting || isSaving) return;
    if (!form.telegramChatId.trim()) {
      setError("Enter a Telegram chat ID before testing Telegram");
      return;
    }
    const input = settingsInputFromForm(form);
    if (!input) {
      setError("Check timeout must be a whole number");
      return;
    }

    setIsTesting(true);
    setError(null);
    try {
      const response = await updateSettings(input);
      setForm({
        dailyCheckTime: response.settings.dailyCheckTime,
        checkTimeoutMs: String(response.settings.checkTimeoutMs),
        telegramChatId: response.settings.telegramChatId,
        publicSubWorkerOrigin: response.settings.publicSubWorkerOrigin
      });
      await testTelegramSettings();
      toast.showToast({ tone: "success", title: "Settings saved and Telegram test sent" });
    } catch (requestError) {
      setError(readRequestError(requestError, "Failed to send Telegram test notification"));
    } finally {
      setIsTesting(false);
    }
  }

  const disabled = isLoading || isSaving || isTesting;

  return (
    <Dialog.Root open={open} onOpenChange={(nextOpen) => (nextOpen ? undefined : onClose())}>
      <Dialog.Portal>
        <Dialog.Backdrop className="dialog-backdrop" />
        <Dialog.Viewport className="dialog-viewport">
          <Dialog.Popup className="dialog-popup">
            <div className="dialog-header">
              <div>
                <Dialog.Title className="dialog-title">Settings</Dialog.Title>
              </div>
              <Dialog.Close className="dialog-close" aria-label="Close">
                <X aria-hidden="true" className="h-4 w-4" />
              </Dialog.Close>
            </div>

            {isLoading ? <div className="mt-5 text-sm text-[#8f877f]">Loading settings...</div> : null}

            {form ? (
              <form className="mt-5 grid gap-4" onSubmit={handleSubmit}>
                <label className="form-field">
                  <span className="form-label">Daily check time</span>
                  <input
                    className="form-input"
                    type="time"
                    value={form.dailyCheckTime}
                    onChange={(event) => updateForm(setForm, { dailyCheckTime: event.target.value })}
                    disabled={disabled}
                    required
                  />
                </label>

                <label className="form-field">
                  <span className="form-label">Check timeout (ms)</span>
                  <input
                    className="form-input"
                    type="number"
                    min="1000"
                    max="30000"
                    step="500"
                    value={form.checkTimeoutMs}
                    onChange={(event) => updateForm(setForm, { checkTimeoutMs: event.target.value })}
                    disabled={disabled}
                    required
                  />
                </label>

                <label className="form-field">
                  <span className="form-label">Telegram chat ID</span>
                  <input
                    className="form-input"
                    value={form.telegramChatId}
                    onChange={(event) => updateForm(setForm, { telegramChatId: event.target.value })}
                    disabled={disabled}
                    autoComplete="off"
                  />
                </label>

                <label className="form-field">
                  <span className="form-label">Public fallback origin</span>
                  <input
                    className="form-input"
                    type="url"
                    value={form.publicSubWorkerOrigin}
                    onChange={(event) => updateForm(setForm, { publicSubWorkerOrigin: event.target.value })}
                    disabled={disabled}
                    required
                    autoComplete="off"
                  />
                </label>

                {error ? (
                  <div role="alert" className="inline-error">
                    {error}
                  </div>
                ) : null}

                <div className="dialog-actions">
                  <Dialog.Close className="secondary-button" type="button" disabled={disabled}>
                    Close
                  </Dialog.Close>
                  <button
                    className="secondary-button"
                    type="button"
                    onClick={handleTestTelegram}
                    disabled={disabled || isTesting || !form.telegramChatId.trim()}
                  >
                    <Send aria-hidden="true" className="h-4 w-4" />
                    {isTesting ? "Testing..." : "Test Telegram"}
                  </button>
                  <button className="accent-button" type="submit" disabled={disabled}>
                    {isSaving ? "Saving..." : "Save"}
                  </button>
                </div>
              </form>
            ) : null}

            {!isLoading && !form && error ? (
              <div role="alert" className="mt-5 inline-error">
                {error}
              </div>
            ) : null}
          </Dialog.Popup>
        </Dialog.Viewport>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function updateForm(
  setForm: Dispatch<SetStateAction<SettingsForm | null>>,
  patch: Partial<SettingsForm>
) {
  setForm((current) => (current ? { ...current, ...patch } : current));
}

function settingsInputFromForm(form: SettingsForm): SettingsInput | undefined {
  const checkTimeoutMs = Number(form.checkTimeoutMs);
  if (!Number.isInteger(checkTimeoutMs)) {
    return undefined;
  }

  return {
    dailyCheckTime: form.dailyCheckTime,
    checkTimeoutMs,
    telegramChatId: form.telegramChatId,
    publicSubWorkerOrigin: form.publicSubWorkerOrigin
  };
}

function readRequestError(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return error.message;
  return fallback;
}
